import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { lookupItemPrice, lookupItemPriceErrorMessage, type QuotationItemDto } from '../lib/quotationApi';
import { formatPricingSource } from '../features/quotations/pricingSourceLabel';
import { ceilPrice } from '../utils/priceUtils';
import { markupCostUsd, sellingPriceAt } from '../utils/pricing';
import { LINE_COST_NEEDS_VENDOR_MESSAGE, isLineCostNeedsVendorError, lineHasRealVendor } from '../features/quotations/lineVendor';
import { markupEditProblem } from '../features/quotations/lineEditChecks';
import { lookupNoPriceReason } from '../features/quotations/lookupNoPriceReason';

interface AddQuotationItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with the prepared new item. The parent appends it to the quotation. It is priced only
   * when the lookup named a real vendor for it (D107); otherwise it has no cost, and the parent
   * opens the Master Catalogue popup so it can be priced with a vendor.
   */
  onAdd: (item: Omit<QuotationItemDto, 'lineNumber'>) => Promise<void> | void;
  defaultMarkupPct?: number;
}

// L/ML/GAL/PT were missing entirely — found live-verifying the atomic_volume
// basis (a can's price re-quoted per litre off its Master Catalogue Unit
// Size): the RFQ-generation path can ask getItemPricing for any unit text an
// RFQ states, unconstrained by this list, but this modal's dropdown was the
// only way to REACH that unit on the manual "Add Item" path — so the backend
// capability existed with no UI path to it here. Matches
// pricingBasis.service.js's VOLUME_UNITS exactly.
//
// NOT the same list as QuotationDetailPage's INDUSTRY_UNIT_OPTIONS, and the two
// must not be "unified" on the grounds that both name units. They answer
// different questions:
//
//   this one  — "what unit is the CLIENT asking in", which is the client's to
//               state. No conversion happens, so a per-ML or per-PT line is
//               perfectly quotable even where it cannot be RESTATED (a pint's
//               size is undecided here — TECH_DEBT #144; a gallon is four
//               litres since 2026-09-14).
//   that one  — "restate this already-priced line in a DIFFERENT unit", which
//               needs a real ratio and a price the cent can hold. G and ML are
//               excluded there for precisely that reason and belong here.
//
// Trimmed 2026-08-27 at the user's request: EA, UNIT, PACK, BOX, CARTON, BAG
// and TIN are seven names for the same outer thing, and INNER is a synthetic
// label this system generates, never something a vessel writes on an RFQ. G and
// OZ added in the same pass — an RFQ can already state them, so the manual path
// being unable to was the inconsistency, not their presence.
const UOM_OPTIONS = ['PCS', 'CASE', 'KG', 'G', 'LB', 'OZ', 'L', 'ML', 'GAL', 'PT'];

// `ceilPrice` from utils/priceUtils, not a local `ceil2`. The copy this
// replaced was a bare `Math.ceil(n * 100) / 100` sitting one directory away
// from the owner that exists precisely because that expression is wrong:
// without the 6-decimal quantize, 7.44 x 40 (297.60000000000002 in IEEE-754)
// ceils to 297.61 and Total stops equalling Qty x Unit on the row the user is
// looking at. (TECH_DEBT #66)

export const AddQuotationItemModal: React.FC<AddQuotationItemModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  defaultMarkupPct = 0,
}) => {
  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [quantity, setQuantity] = useState<string>('1');
  const [uom, setUom] = useState('PCS');
  // The unit the PERSON chose — the ask this line records as `originalRequest`.
  // Fetch Price may label the price in its own spelling of the unit ("each"
  // for PCE); that is the price's unit, not what was asked (D51, QUO-00054).
  const [askedUom, setAskedUom] = useState('PCS');
  const [unitPrice, setUnitPrice] = useState<string>('');
  const [markup, setMarkup] = useState<string>(String(defaultMarkupPct ?? 0));
  const [remarks, setRemarks] = useState('');

  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [priceMeta, setPriceMeta] = useState<{
    source: QuotationItemDto['pricingSource'];
    confidence: number;
    selectedVendor: string | null;
    vendorOptions: QuotationItemDto['vendorOptions'];
    currency: string;
    pricingDetails?: Record<string, unknown>;
    internalRemarks?: string | null;
    // TECH_DEBT #61 — set when getItemPricing could not find a real pack
    // ratio between the unit typed above and the unit it actually has a
    // price for. `uom` state is already flipped to the honest unit below
    // (result.uom); these two are what let the grid flag the new line the
    // same way a generated line is flagged.
    unitMismatch?: boolean;
    unitRequested?: string | null;
    unitMismatchReason?: string | null;
    // What the lookup answered, kept so `handleAdd` can tell a price the user
    // ACCEPTED from one they typed over. The pinned TT$ belongs to the looked-up
    // price and to nothing else — a hand-typed US$ figure states US$, and
    // carrying the old TT$ onto it would print the catalogue's number beside a
    // cost that is no longer the catalogue's (`utils/pricing.unitCostTtd`).
    //
    // Named in this list on purpose: a fixed field list between a lookup and a
    // write is where TECH_DEBT #190 lost a whole column, twice.
    unitPrice?: number | null;
    unitPriceTtd?: number | null;
    unitPriceExact?: number | null;
  } | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setItemName('');
      setItemDescription('');
      setQuantity('1');
      setUom('PCS');
      setAskedUom('PCS');
      setUnitPrice('');
      setMarkup(String(defaultMarkupPct ?? 0));
      setRemarks('');
      setPriceMeta(null);
      setPriceError(null);
      setSaveError(null);
      setFetchingPrice(false);
      setSaving(false);
    }
  }, [isOpen, defaultMarkupPct]);

  const qtyNum = Number(quantity) > 0 ? Number(quantity) : 0;
  // D107: no price without a registered vendor. A cost exists on this form only when the lookup
  // named a real vendor for it (and then it may be typed over, as before); anything else is
  // added unpriced and priced afterwards through the Master Catalogue popup, vendor required.
  const vendorKnown = lineHasRealVendor({ selectedVendor: priceMeta?.selectedVendor });
  const unitPriceNum = vendorKnown && unitPrice !== '' ? Number(unitPrice) : null;
  const markupNum = Number(markup) || 0;
  const markupProblem = markupEditProblem(markup);
  // Why Add Item is greyed, said beside it (B020): a disabled button with no reason read as broken.
  const addBlockedReason = !itemName.trim()
    ? 'Enter an item name to add this item.'
    : qtyNum <= 0
      ? 'Enter a quantity greater than 0 to add this item.'
      : markupProblem;

  const totalCost = unitPriceNum != null && qtyNum > 0 ? ceilPrice(unitPriceNum * qtyNum) : null;
  // `sellingPriceAt` — the owner's one expression for "what does this rate cost".
  // A local copy of it here would be a price the rest of the system could not
  // recognise as the rate's own: `resolveMarkupPercent` decides what a line is
  // quoted at by recomputing the price and comparing.
  //
  // The person kept the looked-up price: the TT$ the lookup's source stated is pinned onto
  // the line exactly as a generated line pins it, and the UNROUNDED cost that price was
  // ceiled from (D85) is stored beside it, so the markup is applied to that and rounded up
  // once. Typing a figure of their own over it states a cost afresh: both are null, because
  // the pin and the exact then belong to a price this line no longer has.
  const keptLookup = unitPriceNum != null && priceMeta?.unitPrice != null && unitPriceNum === priceMeta.unitPrice;
  const pinnedTtd = keptLookup ? priceMeta?.unitPriceTtd ?? null : null;
  const unitPriceExact = keptLookup ? priceMeta?.unitPriceExact ?? null : null;
  const unitSelling =
    unitPriceNum != null ? sellingPriceAt(markupCostUsd(unitPriceNum, unitPriceExact), markupNum) : null;
  // Extended from the ROUNDED unit selling price, the same arithmetic `resolveItemPricing`
  // and `Quotation.pre('save')` use. This modal is a PREVIEW of what the save will store, so
  // deriving it from the total cost instead showed 131.56 for a line that then stored 131.60
  // — the preview disagreeing with the row it creates (USC-BUG-0038).
  const totalSelling =
    totalCost != null && unitSelling != null ? ceilPrice(unitSelling * qtyNum) : null;

  const handleFetchPrice = async () => {
    setPriceError(null);
    if (!itemName.trim()) {
      setPriceError('Enter an item name first.');
      return;
    }
    setFetchingPrice(true);
    try {
      const result = await lookupItemPrice({
        itemName: itemName.trim(),
        quantity: qtyNum > 0 ? qtyNum : 1,
        uom,
      });
      if (result.unitPrice != null && !lineHasRealVendor({ selectedVendor: result.selectedVendor })) {
        // A price with no real vendor behind it is not taken (D107). Nothing of the lookup is
        // kept either: its source and details describe a price this line does not have.
        setUnitPrice('');
        setPriceMeta(null);
        setPriceError(LINE_COST_NEEDS_VENDOR_MESSAGE);
        return;
      }
      if (result.unitPrice != null) {
        setUnitPrice(String(result.unitPrice));
      } else {
        // Found but refused says why (D69 pack conflict, D51 no unit ratio). "No price found"
        // sent people hunting for a price that exists.
        setPriceError(
          lookupNoPriceReason(result, uom) ??
            'No price found in historical quotations, Yelo, price lists, or vendor invoices.'
        );
      }
      if (result.uom) setUom(String(result.uom).toUpperCase());
      setPriceMeta({
        source: result.pricingSource,
        confidence: result.pricingConfidence,
        selectedVendor: result.selectedVendor ?? null,
        vendorOptions: result.vendorOptions || [],
        currency: result.currency || 'USD',
        pricingDetails: result.pricingDetails,
        internalRemarks: result.internalRemarks ?? null,
        unitMismatch: !!result.unitMismatch,
        unitRequested: result.unitMismatch ? (result.unitRequested ?? uom) : null,
        unitMismatchReason: result.unitMismatch ? (result.unitMismatchReason ?? null) : null,
        unitPrice: result.unitPrice,
        unitPriceTtd: result.unitPriceTtd ?? null,
        unitPriceExact: result.unitPriceExact ?? null,
      });
    } catch (e: any) {
      setPriceError(lookupItemPriceErrorMessage(e, 'Failed to look up item price. Please try again.'));
    } finally {
      setFetchingPrice(false);
    }
  };

  const handleAdd = async () => {
    setSaveError(null);
    if (!itemName.trim()) {
      setSaveError('Item name is required.');
      return;
    }
    if (qtyNum <= 0) {
      setSaveError('Quantity must be greater than zero.');
      return;
    }
    if (markupProblem) {
      setSaveError(markupProblem);
      return;
    }

    const newItem: Omit<QuotationItemDto, 'lineNumber'> = {
      itemName: itemName.trim(),
      itemDescription: itemDescription.trim() || itemName.trim(),
      quantity: qtyNum,
      uom: uom || 'PCS',
      // TECH_DEBT #151 — this modal is a third generation-time construction
      // site (quotationGeneration.service.js has the other two), and this IS
      // the ask: a person typing a quantity/unit directly, the manual
      // equivalent of an RFQ line. Written once, here, never touched again.
      originalRequest: { quantity: qtyNum, uom: askedUom || uom || 'PCS' },
      unitMismatch: priceMeta?.unitMismatch || false,
      unitRequested: priceMeta?.unitRequested ?? null,
      unitMismatchReason: priceMeta?.unitMismatch ? (priceMeta.unitMismatchReason ?? null) : null,
      unitPrice: unitPriceNum ?? undefined,
      unitPriceTtd: pinnedTtd,
      unitPriceExact,
      totalPrice: totalCost ?? undefined,
      currency: priceMeta?.currency || 'USD',
      markup: markupNum,
      unitSellingPrice: unitSelling ?? undefined,
      totalSellingPrice: totalSelling ?? undefined,
      pricingSource: priceMeta?.source || (unitPriceNum != null ? 'client_provided' : 'not_found'),
      pricingConfidence: priceMeta?.confidence ?? (unitPriceNum != null ? 1 : 0),
      pricingDetails: priceMeta?.pricingDetails,
      selectedVendor: priceMeta?.selectedVendor || undefined,
      vendorOptions: priceMeta?.vendorOptions || [],
      remarks: remarks.trim() || undefined,
      internalRemarks: priceMeta?.internalRemarks || undefined,
    };

    setSaving(true);
    try {
      await onAdd(newItem);
      onClose();
    } catch (e: any) {
      // A fixed sentence per code, never the error text (CLAUDE.md invariant 6).
      setSaveError(isLineCostNeedsVendorError(e) ? LINE_COST_NEEDS_VENDOR_MESSAGE : 'Failed to add item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // The shared words for a pricing source. This was a private copy of the map
  // that had not learned 'master_catalogue', so a hand-entered row printed its
  // raw key here.
  const sourceLabel = (s?: string | null) => formatPricingSource(s);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Item to Quotation" maxWidthClass="max-w-2xl">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Item Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="e.g. Marine Diesel Filter 10 micron"
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Description (optional)</label>
            <textarea
              value={itemDescription}
              onChange={(e) => setItemDescription(e.target.value)}
              rows={2}
              placeholder="Additional details, brand, spec, etc."
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Quantity <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={0}
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Unit (UOM)</label>
            <select
              value={uom}
              onChange={(e) => {
                // A price fetched for one unit is not a price for another: a
                // unit changed after Fetch Price used to keep the old figure and
                // save it per the new unit (D51, QUO-00054). Fetch again.
                setUom(e.target.value);
                setAskedUom(e.target.value);
                if (priceMeta) {
                  setPriceMeta(null);
                  setUnitPrice('');
                  setPriceError('The unit changed, so the fetched price was cleared. Fetch the price again for ' + e.target.value + '.');
                }
              }}
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {UOM_OPTIONS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-600 mb-1">Unit Cost Price (USD)</label>
                {vendorKnown ? (
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={unitPrice}
                    onChange={(e) => {
                      setUnitPrice(e.target.value);
                      // Manual override clears any auto-fetched pricing source so we mark it as client_provided on save.
                      if (priceMeta) setPriceMeta({ ...priceMeta, source: 'client_provided', selectedVendor: priceMeta.selectedVendor });
                    }}
                    placeholder="Click Fetch Price to look up"
                    className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                ) : (
                  // D107: no cost can be typed without a vendor behind it.
                  <p className="text-xs text-slate-500">
                    Fetch Price to price this from a vendor. With no vendor it is added unpriced, and you then
                    add it to the Master Catalogue with its vendor.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleFetchPrice}
                disabled={fetchingPrice || !itemName.trim()}
                className="h-9 px-4 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {fetchingPrice ? 'Fetching…' : 'Fetch Price'}
              </button>
            </div>
            {priceError && <p className="mt-1 text-xs text-red-600">{priceError}</p>}
            {priceMeta && !priceError && (
              <p className="mt-1 text-xs text-slate-500">
                Source: <span className="font-medium text-slate-700">{sourceLabel(priceMeta.source)}</span>
                {priceMeta.selectedVendor ? <> · Vendor: <span className="font-medium text-slate-700">{priceMeta.selectedVendor}</span></> : null}
                {priceMeta.confidence > 0 ? <> · Confidence: {Math.round(priceMeta.confidence * 100)}%</> : null}
                {priceMeta.vendorOptions && priceMeta.vendorOptions.length > 0 ? (
                  <> · {priceMeta.vendorOptions.length} vendor option{priceMeta.vendorOptions.length === 1 ? '' : 's'}</>
                ) : null}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Markup %</label>
            <input
              type="number"
              step="any"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            {markupProblem && <p className="mt-1 text-xs text-red-600">{markupProblem}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Unit Selling Price (USD)</label>
            <input
              type="text"
              value={unitSelling != null ? unitSelling.toFixed(2) : ''}
              readOnly
              className="w-full h-9 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">Remarks (optional)</label>
            <input
              type="text"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Both figures are Qty × the Unit Cost Price typed above, which this form
            takes in US$ (see the input labels) — so they are US$ too. They used to
            print a bare "$" on a platform that carries both TT$ and US$, which is
            the ambiguity reported in ClickUp 86d478wb9: the reader could not tell
            which dollar they were looking at. The currency is now stated in the
            label AND in the figure, because either one alone is missed when the
            row is skimmed. */}
        {(totalCost != null || totalSelling != null) && (
          <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-700 flex gap-6 flex-wrap">
            {totalCost != null && (
              <div>
                Total Cost (USD): <span className="font-semibold text-slate-900">US${totalCost.toFixed(2)}</span>
              </div>
            )}
            {totalSelling != null && (
              <div>
                Total Selling (USD): <span className="font-semibold text-slate-900">US${totalSelling.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {saveError && <div className="text-xs text-red-600">{saveError}</div>}

        <div className="flex justify-end items-center gap-2 pt-2 border-t border-slate-100">
          {addBlockedReason && !saving && (
            <span className="mr-auto text-xs text-slate-500">{addBlockedReason}</span>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 px-4 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving || !!addBlockedReason}
            title={addBlockedReason ?? undefined}
            className="h-9 px-4 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding…' : 'Add Item'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddQuotationItemModal;
